<template>
  <div
    v-if="selectedEmployee"
    class="ev-page-container ev-page-container--narrow"
  >
    <!-- Page Header -->
    <div class="ev-page-header">
      <h1 class="ev-page-title">
        <span class="ev-title-icon">👤</span>
        Detalji zaposlenog
      </h1>
      <p class="ev-page-subtitle">Detaljna administracija zaposlenog</p>
    </div>

    <!-- Employee Info Card -->
    <div class="ev-card">
      <h3 class="ev-section-title">
        <span class="ev-section-icon">📝</span>
        Osnovni podaci
      </h3>

      <div class="ev-form-grid">
        <!-- Id -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">🔑</span>
            ID
          </label>
          <input
            disabled
            class="ev-form-input ev-form-input--disabled"
            type="text"
            v-model="selectedEmployee.employeeId"
          />
        </div>

        <!-- Ime -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">✏️</span>
            Ime
          </label>
          <input
            class="ev-form-input"
            type="text"
            v-model="selectedEmployee.firstName"
          />
        </div>

        <!-- Prezime -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">✏️</span>
            Prezime
          </label>
          <input
            class="ev-form-input"
            type="text"
            v-model="selectedEmployee.lastName"
          />
        </div>

        <!-- Nadređeni -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">👔</span>
            Nadređeni
          </label>
          <vue-select
            class="ev-select"
            type="text"
            label="name"
            :clearable="true"
            :options="activeEmployees"
            v-model="superiorEmployee"
            placeholder="Izaberite nadređenog..."
          />
        </div>

        <!-- Sektor -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">🏢</span>
            Sektor
          </label>
          <vue-select
            class="ev-select"
            type="text"
            label="name"
            :clearable="false"
            :options="departments"
            v-model="selectedEmployee.department"
            placeholder="Izaberite sektor..."
          />
        </div>
      </div>
    </div>

    <!-- Datumi Card -->
    <div class="ev-card">
      <h3 class="ev-section-title">
        <span class="ev-section-icon">📅</span>
        Period važenja
      </h3>

      <div class="ev-form-row">
        <!-- Datum početka -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">▶️</span>
            Datum početka
          </label>
          <date-picker
            v-model="selectedEmployee.startDate"
            class="ev-date-picker"
          />
        </div>
        <!-- Datum kraja -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">⏹️</span>
            Datum kraja
          </label>
          <date-picker
            v-model="selectedEmployee.endDate"
            class="ev-date-picker"
          />
        </div>
      </div>
    </div>

    <!-- Radno vrijeme Card -->
    <div class="ev-card">
      <h3 class="ev-section-title">
        <span class="ev-section-icon">⏰</span>
        Radno vrijeme
      </h3>

      <div class="ev-shift-toggle">
        <label class="ev-toggle-label">
          <input
            type="checkbox"
            v-model="isShiftSpecial"
            class="ev-toggle-checkbox"
          />
          <span class="ev-toggle-slider"></span>
          <span class="ev-toggle-text">Posebno radno vrijeme</span>
        </label>
      </div>

      <div class="ev-form-group" style="margin-top: 12px">
        <label class="ev-form-label">
          <span class="ev-form-label-icon">📋</span>
          Smjena
        </label>
        <vue-select
          class="ev-select"
          type="text"
          label="name"
          :clearable="false"
          :options="shifts"
          :disabled="!isShiftSpecial"
          @option:selected="shiftSelected"
          v-model="selectedShift"
          placeholder="Izaberite smjenu..."
        />
      </div>
    </div>

    <!-- Status Card -->
    <div class="ev-card">
      <h3 class="ev-section-title">
        <span class="ev-section-icon">⚙️</span>
        Status i podešavanja
      </h3>

      <div class="ev-form-row">
        <!-- Status -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">✅</span>
            Status (aktivan)
          </label>
          <div class="ev-shift-toggle">
            <label class="ev-toggle-label">
              <input
                type="checkbox"
                v-model="selectedEmployee.status"
                class="ev-toggle-checkbox"
              />
              <span class="ev-toggle-slider"></span>
              <span class="ev-toggle-text">{{
                selectedEmployee.status ? "Aktivan" : "Neaktivan"
              }}</span>
            </label>
          </div>
        </div>
        <!-- Indeks poretka -->
        <div class="ev-form-group">
          <label class="ev-form-label">
            <span class="ev-form-label-icon">🔢</span>
            Indeks poretka
          </label>
          <input
            class="ev-form-input"
            type="text"
            v-model="selectedEmployee.remoteId"
          />
        </div>
      </div>
    </div>

    <!-- Godišnji odmor -->
    <div class="ev-card">
      <dayoffs-quantity-table :employeeId="selectedEmployee.employeeId" />
    </div>

    <!-- Nagrade / kazne -->
    <div class="ev-card">
      <rewards-table :employeeId="selectedEmployee.employeeId" />
    </div>
  </div>
</template>

<script>
// import { getActiveEmployeesFromDepartmentData } from "../../_helpers/arrangers";

import DatePicker from "vue2-datepicker";
import { EmployeeBack } from "../../models/employee";
import reportService from "../../services/report.service";
import userService from "../../services/user.service";
import DayoffsQuantityTable from "../parts/DayoffsQuantityTable.vue";
import RewardsTable from "../parts/RewardsTable.vue";

const { DateTime } = require("luxon");

export default {
  name: "EditEmployee",

  props: {
    button1Name: String,
    button2Name: String,
    title: String,
    // selectedEmployee: Object,
    departments: Array,
    isSaveAndProceedButtonVisible: Boolean,
    AddOrEdit: Boolean,
  },

  components: {
    DatePicker,
    DayoffsQuantityTable,
    RewardsTable,
  },

  data() {
    return {
      selectedShift: null,
      isShiftSpecial: false,
      activeEmployees: [],
      superiorEmployee: null,
      generateUser: false,
      selectedEmployee: null,
      shifts: [],
    };
  },

  async mounted() {
    // load query params if any
    // employeeId param
    console.log(this.$route.query.employeeId);
    const theEmployeeId = this.$route.query.employeeId;
    if (theEmployeeId !== "undefined") {
      this.passedEmployeeId = theEmployeeId;
    }

    console.log("priviledge level");
    console.log(this.$store.state.auth.user.privilegesLevel);

    this.selectedEmployee = (
      await reportService.GetEmployeeByEmployeeId(this.passedEmployeeId)
    ).data;

    // this.selectedEmployee = this.$store.state.general.employees.find(
    //   (e) => e.id == theEmployeeId
    // );

    console.log("selected employee:");
    console.log(this.selectedEmployee);

    // if employee has shiftOverrideId the shift is special
    this.isShiftSpecial =
      this.selectedEmployee.shiftOverrideId != null ? true : false;

    console.log(this.isShiftSpecial);

    var myshifts = (await userService.getShifts()).data;
    console.log("shifts:");
    console.log(this.shifts);

    myshifts.forEach((shift) => {
      this.shifts.push({
        id: shift.shiftId,
        name: shift.name,
      });
    });

    // if shift is not special, selected shift is pulled from department
    if (!this.isShiftSpecial) {
      if (this.shifts.length !== 0) {
        this.selectedShift = this.shifts.filter((shi) => {
          return shi.id == this.selectedEmployee.department.shiftId;
        });
      }
    } else {
      if (this.shifts.length !== 0) {
        this.selectedShift = this.shifts.filter((shi) => {
          return shi.id == this.selectedEmployee.shiftOverrideId;
        });
      }
    }

    // console.log(this.selectedShift);
    console.log("selected EMPLOSEE");
    // console.log(new Date(this.selectedEmployee.startDate));
    this.selectedEmployee.startDate !== null
      ? (this.selectedEmployee.startDate = new Date(
          this.selectedEmployee.startDate
        ))
      : (this.selectedEmployee.startDate = null);
    this.selectedEmployee.endDate !== null
      ? (this.selectedEmployee.endDate = new Date(
          this.selectedEmployee.endDate
        ))
      : (this.selectedEmployee.endDate = null);

    this.superiorEmployee = this.$store.state.general.employees.find(
      (e) => e.id === this.selectedEmployee.reportsTo
    );
    this.activeEmployees = this.$store.state.general.employees;
  },

  methods: {
    shiftSelected() {
      // only if shift is special, put the override shift id
      if (this.isShiftSpecial) {
        this.selectedEmployee.shiftOverrideId = this.selectedShift.id;
      }
    },
    async saveAndCloseButtonClicked() {
      this.arrangeResult();
      this.$emit(
        "saveAndCloseClicked",
        this.selectedEmployee,
        this.generateUser
      );

      console.log(this.selectedEmployee);

      this.selectedEmployee.department.id =
        this.selectedEmployee.department.departmentId;

      const employeeToEdit = new EmployeeBack(this.selectedEmployee);

      console.log(employeeToEdit);

      const result = await userService.putEmployee(employeeToEdit);

      console.log(result);
    },
    arrangeResult() {
      // reprotsTo
      // console.log(this.superiorEmployee);
      this.selectedEmployee.reportsTo =
        this.superiorEmployee == null ? null : this.superiorEmployee.id;

      // startDate
      this.selectedEmployee.startDate !== null
        ? (this.selectedEmployee.startDate = DateTime.fromJSDate(
            this.selectedEmployee.startDate
          )
            .toISO()
            .split("+")[0])
        : null;

      // endDate
      this.selectedEmployee.endDate !== null
        ? (this.selectedEmployee.endDate = DateTime.fromJSDate(
            this.selectedEmployee.endDate
          )
            .toISO()
            .split("+")[0])
        : null;
    },
    canProceed(selectedEmployee) {
      if (selectedEmployee == null) {
        return false;
      }
      if (
        selectedEmployee.firstName == null ||
        selectedEmployee.lastName == null
      ) {
        return false;
      }
      if (
        selectedEmployee.firstName !== "" &&
        selectedEmployee.lastName !== ""
      ) {
        return true;
      }
      return false;
    },
  },
};
</script>

<style scoped>
/* Form grid - single column stacked fields */
.ev-form-grid {
  display: flex;
  flex-direction: column;
  gap: var(--ev-spacing-md);
}

/* Two-column row for dates, status etc */
.ev-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ev-spacing-lg);
}

@media (max-width: 640px) {
  .ev-form-row {
    grid-template-columns: 1fr;
  }
}

.ev-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ev-form-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ev-text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.ev-form-label-icon {
  font-size: 15px;
}

.ev-form-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid var(--ev-border);
  border-radius: var(--ev-radius);
  background: var(--ev-bg);
  font-size: 14px;
  color: var(--ev-text-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.ev-form-input:focus {
  border-color: var(--ev-primary-start);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  outline: none;
}

.ev-form-input--disabled {
  background: var(--ev-border);
  color: var(--ev-text-muted);
  cursor: not-allowed;
}

/* Toggle switch */
.ev-shift-toggle {
  display: flex;
  align-items: center;
}

.ev-toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.ev-toggle-checkbox {
  position: relative;
  width: 44px;
  height: 24px;
  appearance: none;
  -webkit-appearance: none;
  background: var(--ev-border);
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
}

.ev-toggle-checkbox::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.ev-toggle-checkbox:checked {
  background: var(--ev-primary-start);
}

.ev-toggle-checkbox:checked::after {
  transform: translateX(20px);
}

.ev-toggle-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--ev-text-secondary);
}
</style>